import { Link } from "react-router-dom";
import { MonoLabel, Em } from "@/site/components/Atoms";
import PipelineH from "@/site/anim/PipelineH";

export default function About() {
  return (
    <>
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20">
        <div className="fade-up"><MonoLabel>ABOUT FOREALCO</MonoLabel></div>
        <h1 className="h-display fade-up delay-1 mt-6 text-[44px] sm:text-[58px] lg:text-[68px] max-w-4xl">
          Built to turn opportunity <Em>into operational reality.</Em>
        </h1>
        <p className="fade-up delay-2 mt-7 max-w-2xl text-[15px] leading-[1.85] text-white/70">
          Forealco was created to help businesses transform cross-border market potential into
          structured, locally supported, and commercially viable business routes. It focuses on the
          space between opportunity insight and practical execution: where market interest must be
          tested, structured, coordinated, and grounded before it can become a working business
          path.
        </p>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-16">
        <PipelineH height={180} />
      </section>

      <Section title="Why Forealco Exists" kicker="01">
        <p>
          Many companies can see that a market looks attractive. Fewer can identify the right way
          to enter it, structure it, coordinate support layers, and turn the opportunity into an
          operational route.
        </p>
        <p>This gap is where Forealco operates.</p>
        <p>
          A market report can describe conditions. A local introduction can open a conversation. A
          company formation service can create an entity. But none of these elements, by
          themselves, create a commercially viable market position.
        </p>
        <p className="text-white/60">
          Forealco exists to help bridge this gap: from market interest to structure, from
          structure to coordination, and from coordination toward practical execution.
        </p>
      </Section>

      <Section title="What Forealco Focuses On" kicker="02">
        <p>
          Forealco combines opportunity mapping, market-entry structuring, controlled coordination,
          institutional understanding, trade logic, local support alignment, and business
          infrastructure thinking.
        </p>
        <p>
          Its role is not to act as a generic consulting firm, an open referral marketplace, or a
          simple company formation provider. Its role is to help define whether an opportunity can
          become a credible route, which structure it requires, which support layers must be
          aligned, and what practical steps are needed before moving forward.
        </p>
        <p className="text-white/60">
          Forealco focuses on commercially relevant opportunities where structure, feasibility,
          local fit, partner readiness, institutional context, and execution logic matter.
        </p>
      </Section>

      <Section title="Kosovo as the First Active Landscape" kicker="03">
        <p>
          Kosovo is Forealco's first active opportunity landscape, not its limitation.
        </p>
        <p>
          It offers a developing business environment where market entry, advisory coordination,
          digital business infrastructure, service commercialization, institutional opportunities,
          defense-related support, trade coordination, and selected strategic sectors can be
          evaluated together.
        </p>
        <p>
          Forealco does not present Kosovo as a shortcut or a generic low-tax destination. Kosovo
          is evaluated as a practical base where commercial purpose, local coordination, partner
          readiness, institutional context, and execution capacity must be tested together.
        </p>
        <p className="text-white/60">
          The same approach can later be applied to other markets where structured entry, selected
          local support, institutional understanding, trade logic, digital infrastructure, and
          business coordination can create practical value.
        </p>
      </Section>

      <Section title="Experience Behind the Approach" kicker="04">
        <p>
          Forealco's approach is informed by senior-level experience in international contracting,
          regional operations, program management, institutional environments, and cross-border
          business development.
        </p>
        <p>
          This includes substantial experience in Kosovo's international operating environment,
          where large-scale maintenance, support, infrastructure coordination, and multi-layered
          service delivery were managed in connection with NATO/KFOR-related facilities and
          operational requirements.
        </p>
        <p>
          This background matters because Forealco's work is not based only on abstract market
          analysis. It is shaped by practical exposure to institutional expectations, operational
          continuity, local execution, documentation discipline, reliability, and multi-party
          coordination.
        </p>
        <p className="text-white/60">
          That experience strengthens Forealco's focus on structure, discretion, credibility, and
          execution discipline.
        </p>
      </Section>

      <Section title="Erenown" kicker="05">
        <p>
          Forealco operates within the wider Erenown brand environment, while maintaining its own
          focus on opportunity realization, market-entry coordination, and structured business
          development.
        </p>
        <p>
          Within this wider environment, Forealco acts as the market access and opportunity
          structuring line. Where relevant, it can coordinate with specialized brands within the
          Erenown ecosystem, including Hubinall and Hangonin, for digital workflows, advisory-office
          tools, local presence support, practical landing needs, and operational infrastructure.
        </p>
        <p className="text-white/60">
          This relationship allows Forealco to remain focused on route design and opportunity
          structuring, while connecting selected opportunities with relevant execution layers when
          required.
        </p>
      </Section>

      <section
        data-testid="about-final-cta"
        className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28"
      >
        <div className="border border-white/10 bg-white/[0.025] p-10 md:p-14 relative overflow-hidden">
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, #9DB4FF 50%, transparent)",
              opacity: 0.5,
            }}
            aria-hidden="true"
          />
          <h2 className="h-display text-[30px] md:text-[44px] leading-[1.12] max-w-4xl">
            Let's shape the right structure <Em>for your opportunity.</Em>
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-[1.9] text-white/70">
            Whether you are evaluating Kosovo as a first step, considering a cross-border business
            route, exploring institutional or sector-specific opportunities, or looking for a
            structured way to enter a developing market, Forealco can help frame the opportunity,
            test the route, and define the next practical move.
          </p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Link
              to="/explore-opportunities"
              data-testid="about-cta-primary"
              className="btn-primary"
            >
              Explore Opportunities →
            </Link>
            <Link
              to="/approach"
              data-testid="about-cta-secondary"
              className="btn-secondary"
            >
              Our Approach
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Section({ title, kicker, children }) {
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-20 grid grid-cols-12 gap-10">
      <div className="col-span-12 lg:col-span-4">
        <MonoLabel>{kicker}</MonoLabel>
        <h2 className="h-display mt-4 text-[28px] md:text-[34px] leading-[1.15]">{title}</h2>
      </div>
      <div className="col-span-12 lg:col-span-8 space-y-4 text-[14px] leading-[1.85] text-white/70 max-w-2xl">
        {children}
      </div>
    </section>
  );
}
